import { useEffect, useState, useCallback, useMemo } from "react";
import { DynamicDatePicker } from "../../../components/DatePicker";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { RajasthanDiscomData } from "../../../models/rajasthan";
import {
  fetchRvpnData,
  getRajasthanDiscomData,
  getRajasthanForecastDiscomData,
} from "../Utility/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { useDispatch, useSelector } from "react-redux";
// import { R_CHART_TYPES } from "../const";
import { H_CHART_TYPES } from "../../Hppc/const";
import { GnaRuvnlFooter } from "../components";
import { DEFAULT_DATES, DataTabs, DEMAND_UNITS } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import { formatDateForAPI } from "../const";
import { FilterTab } from "../../../components/Filters";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";

import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanSummaryAnalysis } from "./TrendSummary";
import { RajasthanPeakAnalysis } from "./TrendPeak_Analysis";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { getTrendMetrics } from "../Utility/RestApi";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
const GROUP_BY = [
  "Discom Load",
  "Total Discom Load",
  "RVPN Drawal",
  "Total Load & RVPN Drawal",
];
const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL" },
  { label: "AVVNL", key: "AVVNL" },
];

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const TYPE = ["Drawal", "Schedule", "Load Shedding", "Total Load"];
const UNITS = DEMAND_UNITS;

export function RajasthanTrend() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<TrendData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[2]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedForecastType, setSelectedForecastType] = useState<any[]>([
    "Actual",
    "Forecast D+1",
  ]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const uniqueDate = useMemo(
    () => DefaultDates[activeTab]?.trendDateOptions || [],
    [DefaultDates, activeTab],
  );
  let displayName = selectedGroupKeys;

  if (
    selectedGroupKeys === "RVPN Drawal" &&
    selectedDiscom &&
    selectedDiscom.length > 0
  ) {
    displayName = `${selectedDiscom[0]} Drawal`;
  }
  const dispatch = useDispatch();

  const discomLabel = selectedDiscom.length > 0 ? selectedDiscom[0] : "RVPN";

  const chartSuffixes = useMemo(
    () =>
      selectedGroupKeys === "RVPN Drawal"
        ? [
            {
              suffix: "drawal",
              displayName: `${discomLabel} Drawal`,
              stackSuffix: "rvpnl_drawal",
            },
            {
              suffix: "deviation",
              displayName: "Deviation %",
              stackSuffix: "deviation",
            },
          ]
        : selectedGroupKeys === "Total Load & RVPN Drawal"
          ? [
              {
                suffix: "drawal",
                displayName: "Drawal",
                stackSuffix: "drawal",
              },
              {
                suffix: "deviation",
                displayName: "Deviation %",
                stackSuffix: "deviation",
              },
            ]
          : [
              {
                suffix: "schedule",
                displayName: "Schedule",
                stackSuffix: "schedule",
              },
              {
                suffix: "drawal",
                displayName: "Drawal",
                stackSuffix: "drawal",
              },
              {
                suffix: "load_shedding",
                displayName: "Load Shedding",
                stackSuffix: "load_shedding",
              },
              {
                suffix: "total",
                displayName: "Total Load",
                stackSuffix: "total",
              },
            ],
    [selectedGroupKeys, discomLabel],
  );

  const [selectedSuffix, setSelectedSuffix] = useState(
    chartSuffixes.slice(0, 2),
  );

  useEffect(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      setSelectedSuffix(chartSuffixes);
      setSelectedForecastType(["Actual", "Forecast D+1"]);
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const drawalSuffix = chartSuffixes.filter((s) => s.suffix === "drawal");
      setSelectedSuffix(drawalSuffix);
      setSelectedForecastType(["Actual"]);
    } else {
      setSelectedSuffix(chartSuffixes.slice(0, 2));
    }
  }, [chartSuffixes, selectedGroupKeys]);

  const createChartObject = useCallback(
    (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string,
      selectedXaxis: { isTime: boolean },
      selectedChartType: { value: string; stackId?: string },
      ExchangeColors: string[],
    ) => {
      const displayKey = normalizeDisplayLabelWithoutUndefined(key)
        .replace(/T\+1/g, "D+1")
        .replace(/T\+2/g, "D+2")
        .replace(/rvpn/g, discomLabel)
        .replace(/total_discom/g, "Total Discom");

      return {
        dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
        name: `${displayKey} ${displayName}`,
        yAxisId: suffix === "deviation" ? "right" : undefined,
        stroke:
          suffix === "schedule"
            ? lightenColor(ExchangeColors[index], 25)
            : suffix === "drawal"
              ? ExchangeColors[index]
              : suffix === "deviation"
                ? "#ff6b6b"
                : suffix === "total" &&
                    selectedGroupKeys === "Total Load & RVPN Drawal"
                  ? lightenColor(ExchangeColors[index], 35)
                  : suffix === "total"
                    ? lightenColor(ExchangeColors[index], 15)
                    : suffix === "load_shedding"
                      ? lightenColor(ExchangeColors[index], 45)
                      : undefined,
        type: selectedChartType.value,
        stackId: undefined,
        // selectedChartType.stackId !== undefined ? stackSuffix : undefined,
      };
    },
    [selectedUnit, selectedGroupKeys, discomLabel],
  );

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;

      const ranges = originalDates
        .filter((date: any) => {
          // Skip forecast entries — value has 3 parts: "start,end,Model 1"
          const commaParts = date.value.split(",");
          return commaParts.length <= 2;
        })
        .map((date: any) => {
          let start: string;
          let end: string;

          if (date.value.includes(" to ")) {
            [start, end] = date.value
              .split(" to ")
              .map((s: string) => s.trim());
          } else {
            const parts = date.value.split(",").map((s: string) => s.trim());
            start = parts[0];
            end = parts[1] ?? parts[0];
          }

          return {
            startDate: formatDateForAPI(new Date(start)),
            endDate: formatDateForAPI(new Date(end)),
            blocks: selectedBlock.map((block: any) => block.value),
          };
        })
        .filter((range: any) => range.startDate && range.endDate);
      if (ranges.length === 0) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }
      const discom = selectedDiscom.length === 0 ? "Total" : selectedDiscom[0];
      const metricsData = await getTrendMetrics({
        ranges,
        aggregated: true,
        discom,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock, selectedDiscom]);
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;
      let processedDates = [...originalDates];

      const modelsToAdd = [];
      for (const date of originalDates) {
        const { label, value } = date;
        if (label.replace(" to ", ",") === value) {
          modelsToAdd.push({
            forecast: true,
            isFixed: false,
            label: label + " (Forecast)",
            value: value + ",Model 1",
            model: "Model 1",
          });
        }
      }
      processedDates = [...processedDates, ...modelsToAdd];

      const promises = processedDates.map(async (date: any) => {
        try {
          const [startDate, endDate, model] = date.value.split(",");

          if (model) {
            if (
              selectedGroupKeys !== "RVPN Drawal" &&
              selectedGroupKeys !== "Total Load & RVPN Drawal"
            ) {
              return [];
            }
            const discom =
              selectedDiscom.length === 0 ? "Total" : selectedDiscom[0];
            const forecastedData = await getRajasthanForecastDiscomData(
              startDate,
              endDate,
              model,
              discom,
            );

            if (selectedGroupKeys === "Total Load & RVPN Drawal") {
              return forecastedData.map((item: any) => ({
                ...item,
                dates: date.label.replace(")", " ") + item.forcast_type + ")",
                discom: "rvpn",
                block: item.slot_id || item.block,
              }));
            } else {
              return forecastedData.map((item: any) => ({
                ...item,
                dates: date.label.replace(")", " ") + item.forcast_type + ")",
              }));
            }
          } else {
            if (selectedGroupKeys === "RVPN Drawal") {
              const discom =
                selectedDiscom.length === 0 ? "Total" : selectedDiscom[0];
              return (await fetchRvpnData(startDate, endDate, discom)).map(
                (item: any) => ({
                  ...item,
                  dates: date.label,
                  drawal_mwh: item.drawal_mw / 4,
                  drawal_mw: item.drawal_mw,
                  drawal_lu: item.drawal_mw / 400,
                  block: item.slot_id,
                  discom: "total",
                  total_mwh: (item.load_shedding_mw + item.drawal_mw) / 4,
                  total_mw: item.load_shedding_mw + item.drawal_mw,
                }),
              );
            } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
              const [discomData, rvpnData] = await Promise.all([
                getRajasthanDiscomData(startDate, endDate),
                fetchRvpnData(startDate, endDate),
              ]);

              const discomMapped = discomData.map((item) => ({
                ...item,
                dates: date.label,
                discom: "total_discom",
              }));

              const rvpnMapped = rvpnData.map((item: any) => ({
                ...item,
                dates: date.label,
                drawal_mwh: item.drawal_mw / 4,
                drawal_mw: item.drawal_mw,
                drawal_lu: item.drawal_mw / 400,
                block: item.slot_id,
                discom: "rvpn",
                schedule_mw: 0,
                schedule_mwh: 0,
                schedule_lu: 0,
                load_shedding_mw: 0,
                load_shedding_mwh: 0,
                load_shedding_lu: 0,
                total_mwh: (item.drawal_mw || 0) / 4,
                total_mw: item.drawal_mw || 0,
                total_lu: (item.drawal_mw || 0) / 400,
              }));

              const discomMappedConverted = discomMapped.map((item: any) => ({
                ...item,
                schedule_mwh: (item.schedule_mw || 0) / 4,
                schedule_lu: (item.schedule_mw || 0) / 400,
                drawal_mwh: (item.drawal_mw || 0) / 4,
                drawal_lu: (item.drawal_mw || 0) / 400,
                load_shedding_mwh: (item.load_shedding_mw || 0) / 4,
                load_shedding_lu: (item.load_shedding_mw || 0) / 400,
                total_mwh:
                  ((item.load_shedding_mw || 0) + (item.drawal_mw || 0)) / 4,
                total_lu:
                  ((item.load_shedding_mw || 0) + (item.drawal_mw || 0)) / 400,
              }));

              return [...discomMappedConverted, ...rvpnMapped];
            } else {
              return (await getRajasthanDiscomData(startDate, endDate)).map(
                (item: any) => ({
                  ...item,
                  dates: date.label,
                  schedule_mwh: (item.schedule_mw || 0) / 4,
                  schedule_lu: (item.schedule_mw || 0) / 400,
                  drawal_mwh: (item.drawal_mw || 0) / 4,
                  drawal_lu: (item.drawal_mw || 0) / 400,
                  load_shedding_mwh: (item.load_shedding_mw || 0) / 4,
                  load_shedding_lu: (item.load_shedding_mw || 0) / 400,
                  total_mw:
                    (item.load_shedding_mw || 0) + (item.drawal_mw || 0),
                  total_mwh:
                    ((item.load_shedding_mw || 0) + (item.drawal_mw || 0)) / 4,
                  total_lu:
                    ((item.load_shedding_mw || 0) + (item.drawal_mw || 0)) /
                    400,
                }),
              );
            }
          }
        } catch (error) {
          console.error(`Error processing date ${date.label}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const newData = results.flat();

      if (newData.length === 0) {
        console.warn("No data returned from API calls");
      }

      // FIXED: Improved month parsing and added sorting
      let transformedBewData = newData.map((item) => {
        let monthValue = "Unknown";
        let monthSortKey = 999999; // For sorting purposes

        if (item.date_of_schedule) {
          const date = new Date(item.date_of_schedule);
          if (!isNaN(date.getTime())) {
            monthValue = date
              .toLocaleDateString("en-US", {
                month: "short",
                year: "2-digit",
              })
              .replace(" ", "-");
            monthSortKey = date.getFullYear() * 100 + date.getMonth();
          }
        } else if (item.dates) {
          try {
            const dateMatch = item.dates.match(
              /(\d{4}-\d{2}-\d{2})|([A-Z]{3}-\d{2})/i,
            );
            if (dateMatch) {
              const dateStr = dateMatch[0];
              let date: Date | null = null;

              // Handle MMM-YY format specifically
              const mmmYY = dateStr.match(/([A-Z]{3})-(\d{2})/i);
              if (mmmYY) {
                const monthStr = mmmYY[1].toUpperCase();
                const yearStr = mmmYY[2];
                const monthMap: { [key: string]: number } = {
                  JAN: 0,
                  FEB: 1,
                  MAR: 2,
                  APR: 3,
                  MAY: 4,
                  JUN: 5,
                  JUL: 6,
                  AUG: 7,
                  SEP: 8,
                  OCT: 9,
                  NOV: 10,
                  DEC: 11,
                };
                if (monthMap[monthStr] !== undefined) {
                  date = new Date(
                    2000 + parseInt(yearStr),
                    monthMap[monthStr],
                    1,
                  );
                }
              } else {
                // Handle YYYY-MM-DD format
                date = new Date(dateStr);
              }

              if (date && !isNaN(date.getTime())) {
                monthValue = date
                  .toLocaleDateString("en-US", {
                    month: "short",
                    year: "2-digit",
                  })
                  .replace(" ", "-");
                monthSortKey = date.getFullYear() * 100 + date.getMonth();
              }
            }
          } catch (e) {
            console.error("Error parsing date from dates field:", e);
          }
        }

        return {
          ...item,
          total_mw: (item.load_shedding_mw || 0) + (item.drawal_mw || 0),
          month: monthValue,
          monthSortKey: monthSortKey, // Add sort key for chronological ordering
        };
      });

      // FIXED: Sort data chronologically when month view is selected
      // This prevents crossing lines by ensuring data points are in correct order
      if (selectedXaxis.key === "month") {
        transformedBewData = transformedBewData.sort((a, b) => {
          return (a.monthSortKey || 999999) - (b.monthSortKey || 999999);
        });
      }

      setData([...transformedBewData]);
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (error) {
      console.error("Error in data fetching:", error);
    } finally {
      setLoading(false);
    }
  }, [
    DefaultDates,
    activeTab,
    selectedGroupKeys,
    selectedXaxis.key,
    selectedDiscom,
  ]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);

    if (GROUP_BY[index] === "RVPN Drawal") {
      setSelectedForecastType(["Actual", "Forecast D+1"]);
    } else if (GROUP_BY[index] === "Total Load & RVPN Drawal") {
      setSelectedForecastType(["Actual"]);
    }
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  const handleKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      if (selectedXaxis.key === "time") {
        return ["date", "dateIndex", "block"];
      } else if (selectedXaxis.key === "date") {
        return ["date", "dateIndex"];
      } else if (selectedXaxis.key === "month") {
        return ["month"];
      }
    } else {
      if (selectedXaxis.key === "time" || selectedXaxis.key === "date") {
        return selectedXaxis.values;
      }
      return selectedXaxis.values;
    }
  }, [selectedBlock, selectedXaxis]);

  useEffect(() => {
    let filteredData = data;

    if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      filteredData = data;
    } else if (selectedGroupKeys !== "RVPN Drawal") {
      if (selectedDiscom.length > 0) {
        filteredData = data.filter((item) =>
          selectedDiscom.includes(item.discom),
        );
      }
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.block),
      );
    }

    const aggregationKeys = [
      "schedule",
      "drawal",
      "load_shedding",
      "total",
    ].map((item) => item + "_" + selectedUnit.toLowerCase());

    const averageKeys = [
      "schedule_mw",
      "drawal_mw",
      "load_shedding_mw",
      "total_mw",
    ];

    const par: ChartDataParams = {
      keys: handleKey() ?? [],
      tableKeys: ["dateIndex", "block", "month", "date"],
      groupBy:
        selectedGroupKeys === "RVPN Drawal"
          ? ["dates"]
          : selectedGroupKeys === "Total Load & RVPN Drawal"
            ? ["dates", "discom"]
            : ["dates"].concat(
                selectedGroupKeys === "Discom Load" ? ["discom"] : [],
              ),
      volumeKey: ``,
      aggregationKeys,
      averageKeys,
      averageDuplicationKeys: ["block", "dateIndex"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);

    if (selectedGroupKeys === "RVPN Drawal") {
      const processedData = cData.data.map((dataPoint) => {
        const newDataPoint = { ...dataPoint };

        cData.keys.forEach((key) => {
          if (key.includes("Forecast")) {
            const actualDateKey = key
              .replace(" (Forecast T+1)", "")
              .replace(" (Forecast T+2)", "");
            const actualKeyForForecast = `${actualDateKey}_drawal_${selectedUnit.toLowerCase()}`;
            const forecastKey = `${key}_drawal_${selectedUnit.toLowerCase()}`;

            const actual = dataPoint[actualKeyForForecast] ?? 0;
            const forecast = dataPoint[forecastKey] ?? 0;

            let deviation = 0;
            if (actual !== 0 && forecast !== 0) {
              deviation = (Math.abs(actual - forecast) / actual) * 100;
            }

            const deviationKey = `${key}_deviation_${selectedUnit.toLowerCase()}`;
            newDataPoint[deviationKey] = deviation;
          }
        });

        return newDataPoint;
      });

      setChartData(processedData);
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const processedData = cData.data.map((dataPoint) => {
        const newDataPoint = { ...dataPoint };

        cData.keys.forEach((key) => {
          if (key.includes("Forecast") && key.includes("rvpn")) {
            const actualDateKey = key
              .replace(" (Forecast T+1)", "")
              .replace(" (Forecast T+2)", "");

            const actualKeyForForecast = `${actualDateKey}_drawal_${selectedUnit.toLowerCase()}`;
            const forecastKey = `${key}_drawal_${selectedUnit.toLowerCase()}`;

            const actual = dataPoint[actualKeyForForecast] ?? 0;
            const forecast = dataPoint[forecastKey] ?? 0;

            let deviation = 0;
            if (actual !== 0 && forecast !== 0) {
              deviation = (Math.abs(actual - forecast) / actual) * 100;
            }

            const deviationKey = `${key}_deviation_${selectedUnit.toLowerCase()}`;
            newDataPoint[deviationKey] = deviation;
          }
        });

        return newDataPoint;
      });

      setChartData(processedData);
    } else {
      setChartData(cData.data);
    }

    setTableData(cData.tableData || []);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedUnit,
    selectedDiscom,
    selectedArea,
    selectedBlock,
    selectedSuffix,
    handleKey,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function handleUnitChange(unitParam: string) {
    setSelectedUnit(unitParam);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function getTypes() {
    if (selectedGroupKeys === "RVPN Drawal") {
      return ["Actual", "Forecast D+1", "Forecast D+2", "Deviation %"];
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      return ["Drawal", "Actual"];
    } else {
      return ["Drawal", "Schedule", "Load Shedding", "Total Load"];
    }
  }

  function handleTypeChange(selected: string[]) {
    if (selectedGroupKeys === "RVPN Drawal") {
      const forecastTypes = selected.filter((item) =>
        ["Actual", "Forecast D+1", "Forecast D+2"].includes(item),
      );
      const hasDeviation = selected.includes("Deviation %");

      const finalForecastTypes = forecastTypes;

      const drawalSuffix = chartSuffixes.find((s) => s.suffix === "drawal");
      let newSuffixes = drawalSuffix ? [drawalSuffix] : [];

      if (hasDeviation) {
        const deviationSuffix = chartSuffixes.find(
          (s) => s.suffix === "deviation",
        );
        if (deviationSuffix) {
          newSuffixes.push(deviationSuffix);
        }
      }

      setSelectedSuffix(newSuffixes);
      setSelectedForecastType(finalForecastTypes);
      dispatch(setLoadingFilterName(""));
      return;
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const hasDrawal = selected.includes("Drawal");
      const hasActual = selected.includes("Actual");

      let newSuffixes = [];
      if (hasDrawal) {
        const drawalSuffix = chartSuffixes.find((s) => s.suffix === "drawal");
        if (drawalSuffix) {
          newSuffixes.push(drawalSuffix);
        }
      }

      let forecastTypes = [];
      if (hasActual) {
        forecastTypes.push("Actual");
      }

      setSelectedSuffix(newSuffixes);
      setSelectedForecastType(forecastTypes);
      dispatch(setLoadingFilterName(""));
      return;
    }

    setSelectedSuffix(
      chartSuffixes.filter((suffixObj) =>
        selected.includes(suffixObj.displayName),
      ),
    );
  }

  function getSelectedTypes() {
    if (selectedGroupKeys === "RVPN Drawal") {
      return [
        ...selectedForecastType,
        ...(selectedSuffix.find((s) => s.suffix === "deviation")
          ? ["Deviation %"]
          : []),
      ];
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      let types = [];
      if (selectedSuffix.find((s) => s.suffix === "drawal")) {
        types.push("Drawal");
      }
      if (selectedForecastType.includes("Actual")) {
        types.push("Actual");
      }
      return types;
    } else {
      return selectedSuffix.map((item) => item.displayName);
    }
  }

  const legends = useMemo(() => {
    return selectedSuffix
      .flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys
          .filter((key: any) => {
            if (selectedGroupKeys === "Total Load & RVPN Drawal") {
              return key.includes("total_discom") || key.includes("rvpn");
            }
            return true;
          })
          .map((key: any, index: number) => {
            if (selectedGroupKeys === "Total Load & RVPN Drawal") {
              // Exclude all forecast lines (D+1, D+2, T+1, T+2)
              if (key.includes("Forecast")) {
                return null;
              }

              if (suffix === "deviation") {
                if (!key.includes("rvpn") || !key.includes("Forecast")) {
                  return null;
                }
              }

              if (
                key.includes("rvpn") &&
                (suffix === "schedule" ||
                  suffix === "load_shedding" ||
                  suffix === "total")
              ) {
                return null;
              }
              if (key.includes("total_discom") && suffix === "deviation") {
                return null;
              }
            } else if (selectedGroupKeys === "RVPN Drawal") {
              if (suffix === "deviation" && !key.includes("Forecast")) {
                return null;
              }
            } else if (suffix === "deviation") {
              return null;
            }

            return createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              selectedXaxis,
              selectedChartType as any,
              ExchangeColors,
            );
          }),
      )
      .filter((item: any) => item !== null)
      .filter(
        (item: any) =>
          !(item.name?.includes("Forecast") && item.name.includes("Schedule")),
      )
      .filter(
        (item: any) =>
          !(
            item.name?.includes("Forecast") &&
            item.name.includes("Load Shedding")
          ),
      )
      .filter((item: any) => {
        if (selectedGroupKeys === "Total Load & RVPN Drawal") {
          if (
            item.name?.includes("rvpn") &&
            (item.name?.includes("Forecast") ||
              item.name?.includes("Deviation"))
          ) {
            return selectedForecastType.some((type) => {
              const forecastNum = type.replace("Forecast ", "");
              return (
                item.name?.includes(forecastNum) ||
                item.name?.includes(forecastNum.replace("D+", "T+"))
              );
            });
          }
          if (
            item.name?.includes("rvpn") &&
            !item.name?.includes("Forecast") &&
            !item.name?.includes("Deviation")
          ) {
            return selectedForecastType.includes("Actual");
          }
          if (item.name?.includes("total_discom")) {
            return true;
          }
        }

        if (selectedGroupKeys === "RVPN Drawal") {
          if (item.name?.includes("Deviation %")) {
            return selectedForecastType.some((type) => {
              const forecastNum = type.replace("Forecast ", "");
              return (
                item.name?.includes(forecastNum) ||
                item.name?.includes(forecastNum.replace("D+", "T+"))
              );
            });
          }
          if (item.name?.includes("Forecast")) {
            return selectedForecastType.some((type) => {
              const forecastNum = type.replace("Forecast ", "");
              return (
                item.name?.includes(forecastNum) ||
                item.name?.includes(forecastNum.replace("D+", "T+"))
              );
            });
          }
          if (
            !item.name?.includes("Forecast") &&
            !item.name?.includes("Deviation")
          ) {
            return selectedForecastType.includes("Actual");
          }
        }

        if (
          !item.name?.includes("Forecast") &&
          !item.name?.includes("Deviation %")
        ) {
          return selectedForecastType.includes("Actual");
        }

        if (item.name?.includes("Deviation %")) {
          if (selectedForecastType.length === 0) {
            return false;
          }
          return selectedForecastType.some((type) => {
            const forecastNum = type.replace("Forecast ", "");
            return (
              item.name?.includes(forecastNum) ||
              item.name?.includes(forecastNum.replace("D+", "T+"))
            );
          });
        }

        return true;
      });
  }, [
    selectedSuffix,
    chartKeys,
    selectedGroupKeys,
    selectedForecastType,
    selectedXaxis,
    selectedChartType,
    createChartObject,
  ]);

  function summaryExtraKeys() {
    if (selectedXaxis.isTime) {
      return ["block", "dateIndex"];
    } else if (selectedXaxis.key === "date") {
      return ["dateIndex"];
    } else {
      return [selectedXaxis.values[0]];
    }
  }

  const summaryExtraLabels = selectedXaxis.isTime
    ? ["block", "dateIndex"]
    : selectedXaxis.key === "date"
      ? ["dateIndex"]
      : [selectedXaxis.values[0]];

  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      return selectedXaxis.values[0];
    }
  }, [selectedXaxis.key, selectedXaxis.values, selectedBlock.length]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends as any);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);
  const commonSummaryProps = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Discom Load Trend Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
        metrics={metrics}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
        metrics={metrics}
      />
    );
  }
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />
          <FilterMenu
            title="Discom Load"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={(items) => {
              if (selectedGroupKeys === "RVPN Drawal") {
                // If user is trying to select JVVNL
                if (items.includes("JVVNL")) {
                  // Toggle behavior
                  if (selectedDiscom.includes("JVVNL")) {
                    setSelectedDiscom([]); // ✅ unselect on second click
                  } else {
                    setSelectedDiscom(["JVVNL"]); // ✅ select only JVVNL
                  }
                } else {
                  return; // ❌ block other selections
                }
              } else {
                setSelectedDiscom(items); // normal behavior
                dispatch(setLoadingFilterName(""));
              }
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => _handleGroupByChange(index)}
          />
          {selectedGroupKeys !== "Total Load & RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={getTypes()}
              selectedItems={getSelectedTypes()}
              setSelectedItems={handleTypeChange}
            />
          )}
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Trend Analysis"
              subtitle="Fetching forecast vs actual load trend data…"
            />
          ) : (
            <>
              <div className="flex flex-col gap-y-2 justify-between border-gray-200 bg-white ">
                {" "}
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-80">
                    {displayName} ({selectedUnit})
                  </h2>
                  <div className="flex gap-x-6 w-full justify-end">
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                    <button
                      onClick={() => {
                        if (dataTab.label === "Chart") {
                          downloadChartTableCSV({
                            ...commonSummaryProps,
                          });
                        } else if (dataTab.label === "Summary") {
                          downloadSummaryCSV(commonSummaryProps);
                        } else if (dataTab.label === "Table") {
                          downloadChartTableCSV(commonSummaryProps);
                        }
                      }}
                      className="downloadButton"
                    >
                      Download Data
                    </button>
                  </div>
                </div>
              </div>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedXaxis.isTime}
                  onlyTitle={true}
                  secondYAxisDomain={[0, 100]}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  secondYAxisLabel={
                    selectedSuffix.some((s) => s.suffix === "deviation")
                      ? "Deviation %"
                      : undefined
                  }
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  key={`${selectedGroupKeys}-${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
                  xDataKey={xDataKey}
                  secondXDataKey={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? selectedXaxis.values[1]
                      : undefined
                  }
                  xTick={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  DISCOM Energy Reports (RUVITL), Load Shedding Reports (RUVITL)
                </p>
              }
              text="1.   Actual Drawal = Restricted Demand
              Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2.   Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3.  MW = Represents the average (MW) when the selected time interval is Date or Month.
4. RVPN Drawal = Total Drawal of Rajasthan.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
